// The alarm banner pinned in home's fixed header (TOM-512; Mike chose pinned,
// 2026-10-08), while any alarm needs attention: the most severe one, "+N more"
// for the rest. It reads the shell's one alarm-state subscription, and orders
// alarms as the Alarms page does (activeAlarmViews: severity, then newest).
//
// "Needs attention" is the alarm lifecycle's own word (ALERT_STATE_META):
// Active, Silenced and AwaitingAcknowledge. An acknowledged alarm whose
// condition is still present is on the Alarms page, not here. Only an Active
// alarm rings: its icon pulses and Silence shows. Silence posts the same
// command the Alarms page's buttons do (AckSilenceButtons), and Review opens
// the alarm's own page.

import { useMemo, useState } from 'react';

import { api } from '../../api';
import {
  alarmCommandDisabledTitle,
  alarmCommandErrorMessage,
  alarmCommandLabel,
  alarmCommandOfflineMessage,
  canSilence,
  customSetupOptions,
  displayedTitle,
  type ActiveAlarmView,
  type AlarmStateResponse,
} from '../../lib/alarmDisplay';
import { liveValueLine, observationLookup } from '../../lib/alarmLive';
import { alarmsNeedingAttention } from '../../lib/homeAlarm';
import { isBoatOfflineRelayError } from '../../lib/revealStaging';
import { signalDisplayName, signalLabel, type Signal } from '../../lib/signalCatalog';
import { useSignalCatalog } from '../../lib/useSignalCatalog';
import { useUnits } from '../../lib/useUnits';
import { useBoatChrome } from '../app/boatChrome';
import { boatPath } from '../app/shellNav';
import { AlarmBanner } from '../ui/AlarmBanner';

export function HomeAlarmBanner({ boatOffline }: { boatOffline: boolean }) {
  const { alerts } = useBoatChrome();
  const response = alerts.state.kind === 'ready' ? alerts.state.response : null;
  const views = useMemo(() => alarmsNeedingAttention(response), [response]);
  const worst = views[0];
  if (response === null || worst === undefined) return null;
  return (
    <WorstAlarm
      key={worst.id}
      response={response}
      view={worst}
      more={views.length - 1}
      boatOffline={boatOffline}
      onChanged={alerts.refresh}
    />
  );
}

/** Mounted only while an alarm needs attention, so a calm home reads no signal catalog. */
function WorstAlarm({
  response,
  view,
  more,
  boatOffline,
  onChanged,
}: {
  response: AlarmStateResponse;
  view: ActiveAlarmView;
  more: number;
  boatOffline: boolean;
  onChanged: () => void;
}) {
  const { boatId, liveSamples } = useBoatChrome();
  const units = useUnits();
  const catalog = useSignalCatalog(boatId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const row = response.alerts.find((r) => r.definition.id === view.id);
  const measured = useMemo(() => {
    if (row === undefined) return null;
    const byPath = new Map<string, Signal>(catalog.signals.map((s) => [s.path, s]));
    return liveValueLine(row.definition, {
      observationFor: observationLookup({ live: liveSamples, catalog: byPath }),
      unitFor: (path) => byPath.get(path)?.unit ?? liveSamples?.get(path)?.unit,
      units,
    });
  }, [row, catalog.signals, liveSamples, units]);
  // The title as the Alarms page shows it, with the same catalog names and
  // units, so a prefilled title reads rounded here too (TOM-860).
  const title = useMemo(() => {
    if (row === undefined) return view.title;
    const byPath = new Map<string, Signal>(catalog.signals.map((s) => [s.path, s]));
    return displayedTitle(
      row.definition,
      customSetupOptions(row.definition, {
        labelFor: (path) => {
          const signal = byPath.get(path);
          return signal === undefined ? signalLabel(path) : signalDisplayName(signal);
        },
        units,
        unitFor: (path) => byPath.get(path)?.unit,
      })
    );
  }, [row, view.title, catalog.signals, units]);

  async function silence(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/boats/${boatId}/alerts/${view.id}/silence`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
      });
      // The boat runs it on its own evaluator and mirrors the new state back.
      onChanged();
    } catch (err) {
      setError(
        isBoatOfflineRelayError(err)
          ? alarmCommandOfflineMessage('silence')
          : alarmCommandErrorMessage('silence')
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlarmBanner
      testId="home-alarm-banner"
      title={title}
      subtitle={measured ?? view.stateMeta.label}
      ringing={view.state === 'Active'}
      more={more}
      error={error}
      silence={
        canSilence(view.state)
          ? {
              label: alarmCommandLabel('silence', view.severity ?? null),
              title: boatOffline ? alarmCommandDisabledTitle() : undefined,
              disabled: boatOffline || busy,
              onClick: () => void silence(),
            }
          : undefined
      }
      reviewTo={boatPath(boatId, `alarms/${view.id}`)}
    />
  );
}
